import {
  EVENTS,
  GET_CONTACTS,
  CREATE_CONTACT,
  UPDATE_CONTACT,
  DELETE_CONTACT,
  BULK_DELETE_CONTACTS,
  TOGGLE_FAVORITE,
  IMPORT_CONTACTS,
  SEARCH_CONTACTS,
  GET_FAVORITES,
} from "../reducers/addressBook-reducer";
import addressBookService from "../services/addressBookService";
import { LOCAL_STORAGE_KEYS } from "../constants/Constants";
import storage from "../services/storage";

// Error code returned by the server when the user lacks contacts permission
const CONTACTS_NO_PERMISSION_CODE = 1018;

// When the server signals that contacts permission has been revoked, sync the
// cached domain config so ContactsPage re-renders the disabled-state view.
// We stay on /contacts instead of redirecting to /sessions.
const handleNoContactsPermission = (error) => {
  if (error?.code !== CONTACTS_NO_PERMISSION_CODE) return false;
  const domainConfig = storage.get(LOCAL_STORAGE_KEYS.DOMAIN_CONFIGURATION);
  if (domainConfig?.configurations && Number(domainConfig.configurations.contactmanagement) !== 0) {
    domainConfig.configurations.contactmanagement = "0";
    storage.set(LOCAL_STORAGE_KEYS.DOMAIN_CONFIGURATION, domainConfig);
    window.dispatchEvent(new CustomEvent("domainConfigUpdated"));
  }
  return true;
};

// Reset actions
export const resetAddressBookReducer = () => ({
  event: EVENTS.RESET_ADDRESS_BOOK_REDUCER,
  type: null,
});

export const resetCreateContact = () => ({
  event: EVENTS.RESET_CREATE_CONTACT,
  type: null,
});

export const resetUpdateContact = () => ({
  event: EVENTS.RESET_UPDATE_CONTACT,
  type: null,
});

export const resetDeleteContact = () => ({
  event: EVENTS.RESET_DELETE_CONTACT,
  type: null,
});

export const resetImportContacts = () => ({
  event: EVENTS.RESET_IMPORT_CONTACTS,
  type: null,
});

export const resetBulkDelete = () => ({
  event: EVENTS.RESET_BULK_DELETE,
  type: null,
});

// Get Contacts actions
const getContactsStart = () => ({
  event: EVENTS.GET_CONTACTS,
  type: GET_CONTACTS.START,
});

const getContactsSuccess = (data) => ({
  event: EVENTS.GET_CONTACTS,
  type: GET_CONTACTS.SUCCESS,
  payload: data,
});

const getContactsError = (error) => ({
  event: EVENTS.GET_CONTACTS,
  type: GET_CONTACTS.ERROR,
  payload: error,
});

export const getContacts = (params) => async (dispatch) => {
  dispatch(getContactsStart());
  try {
    const response = await addressBookService.getContacts(params);
    dispatch(getContactsSuccess(response));
    return response;
  } catch (error) {
    if (handleNoContactsPermission(error)) return;
    dispatch(getContactsError(error));
    throw error;
  }
};

// Create Contact actions
const createContactStart = () => ({
  event: EVENTS.CREATE_CONTACT,
  type: CREATE_CONTACT.START,
});

const createContactSuccess = (data) => ({
  event: EVENTS.CREATE_CONTACT,
  type: CREATE_CONTACT.SUCCESS,
  payload: data,
});

const createContactError = (error) => ({
  event: EVENTS.CREATE_CONTACT,
  type: CREATE_CONTACT.ERROR,
  payload: error,
});

export const createContact = (contactData) => async (dispatch) => {
  dispatch(createContactStart());
  try {
    const response = await addressBookService.createContact(contactData);
    dispatch(createContactSuccess(response));
    return response;
  } catch (error) {
    dispatch(createContactError(error));
    throw error;
  }
};

// Update Contact actions
const updateContactStart = () => ({
  event: EVENTS.UPDATE_CONTACT,
  type: UPDATE_CONTACT.START,
});

const updateContactSuccess = (data) => ({
  event: EVENTS.UPDATE_CONTACT,
  type: UPDATE_CONTACT.SUCCESS,
  payload: data,
});

const updateContactError = (error) => ({
  event: EVENTS.UPDATE_CONTACT,
  type: UPDATE_CONTACT.ERROR,
  payload: error,
});

export const updateContact = (contactId, contactData) => async (dispatch) => {
  dispatch(updateContactStart());
  try {
    const response = await addressBookService.updateContact(contactId, contactData);
    dispatch(updateContactSuccess(response));
    return response;
  } catch (error) {
    dispatch(updateContactError(error));
    throw error;
  }
};

// Delete Contact actions
const deleteContactStart = () => ({
  event: EVENTS.DELETE_CONTACT,
  type: DELETE_CONTACT.START,
});

const deleteContactSuccess = (data) => ({
  event: EVENTS.DELETE_CONTACT,
  type: DELETE_CONTACT.SUCCESS,
  payload: data,
});

const deleteContactError = (error) => ({
  event: EVENTS.DELETE_CONTACT,
  type: DELETE_CONTACT.ERROR,
  payload: error,
});

export const deleteContact = (contactId) => async (dispatch) => {
  dispatch(deleteContactStart());
  try {
    const response = await addressBookService.deleteContact(contactId);
    dispatch(deleteContactSuccess({ contactId, success: response }));
    return response;
  } catch (error) {
    dispatch(deleteContactError(error));
    throw error;
  }
};

// Bulk Delete Contacts actions
const bulkDeleteContactsStart = () => ({
  event: EVENTS.BULK_DELETE_CONTACTS,
  type: BULK_DELETE_CONTACTS.START,
});

const bulkDeleteContactsSuccess = (data) => ({
  event: EVENTS.BULK_DELETE_CONTACTS,
  type: BULK_DELETE_CONTACTS.SUCCESS,
  payload: data,
});

const bulkDeleteContactsError = (error) => ({
  event: EVENTS.BULK_DELETE_CONTACTS,
  type: BULK_DELETE_CONTACTS.ERROR,
  payload: error,
});

export const bulkDeleteContacts = (contactIds) => async (dispatch) => {
  dispatch(bulkDeleteContactsStart());
  try {
    const response = await addressBookService.bulkDeleteContacts(contactIds);
    dispatch(bulkDeleteContactsSuccess(response));
    return response;
  } catch (error) {
    dispatch(bulkDeleteContactsError(error));
    throw error;
  }
};

// Toggle Favorite actions
const toggleFavoriteStart = () => ({
  event: EVENTS.TOGGLE_FAVORITE,
  type: TOGGLE_FAVORITE.START,
});

const toggleFavoriteSuccess = (data) => ({
  event: EVENTS.TOGGLE_FAVORITE,
  type: TOGGLE_FAVORITE.SUCCESS,
  payload: data,
});

const toggleFavoriteError = (error) => ({
  event: EVENTS.TOGGLE_FAVORITE,
  type: TOGGLE_FAVORITE.ERROR,
  payload: error,
});

export const toggleFavorite = (contactId, isFavorite) => async (dispatch) => {
  dispatch(toggleFavoriteStart());
  try {
    const response = await addressBookService.toggleFavorite(contactId, isFavorite);
    dispatch(toggleFavoriteSuccess(response));
    return response;
  } catch (error) {
    dispatch(toggleFavoriteError(error));
    throw error;
  }
};

// Import Contacts actions
const importContactsStart = () => ({
  event: EVENTS.IMPORT_CONTACTS,
  type: IMPORT_CONTACTS.START,
});

const importContactsSuccess = (data) => ({
  event: EVENTS.IMPORT_CONTACTS,
  type: IMPORT_CONTACTS.SUCCESS,
  payload: data,
});

const importContactsError = (error) => ({
  event: EVENTS.IMPORT_CONTACTS,
  type: IMPORT_CONTACTS.ERROR,
  payload: error,
});

export const importContacts = (file) => async (dispatch) => {
  dispatch(importContactsStart());
  try {
    const response = await addressBookService.importContacts(file);
    dispatch(importContactsSuccess(response));
    return response;
  } catch (error) {
    dispatch(importContactsError(error));
    throw error;
  }
};

// Get Favorites actions
const getFavoritesStart = () => ({
  event: EVENTS.GET_FAVORITES,
  type: GET_FAVORITES.START,
});

const getFavoritesSuccess = (data) => ({
  event: EVENTS.GET_FAVORITES,
  type: GET_FAVORITES.SUCCESS,
  payload: data,
});

const getFavoritesError = (error) => ({
  event: EVENTS.GET_FAVORITES,
  type: GET_FAVORITES.ERROR,
  payload: error,
});

export const getFavorites = (params) => async (dispatch) => {
  dispatch(getFavoritesStart());
  try {
    const response = await addressBookService.getContacts({ ...params, favoritesOnly: true });
    dispatch(getFavoritesSuccess(response));
    return response;
  } catch (error) {
    if (handleNoContactsPermission(error)) return;
    dispatch(getFavoritesError(error));
    throw error;
  }
};

// Search Contacts actions (for ContactPicker)
const searchContactsStart = () => ({
  event: EVENTS.SEARCH_CONTACTS,
  type: SEARCH_CONTACTS.START,
});

const searchContactsSuccess = (data) => ({
  event: EVENTS.SEARCH_CONTACTS,
  type: SEARCH_CONTACTS.SUCCESS,
  payload: data,
});

const searchContactsError = (error) => ({
  event: EVENTS.SEARCH_CONTACTS,
  type: SEARCH_CONTACTS.ERROR,
  payload: error,
});

export const searchContacts = (query, limit) => async (dispatch) => {
  dispatch(searchContactsStart());
  try {
    const response = await addressBookService.searchContacts(query, limit);
    dispatch(searchContactsSuccess(response));
    return response;
  } catch (error) {
    dispatch(searchContactsError(error));
    throw error;
  }
};

// Export contacts (no redux state needed, just returns data)
export const exportContacts = () => async () => {
  try {
    const response = await addressBookService.exportContacts();
    return response;
  } catch (error) {
    throw error;
  }
};

// Increment session count
export const incrementSessionCount = (contactId) => async () => {
  try {
    const response = await addressBookService.incrementSessionCount(contactId);
    return response;
  } catch (error) {
    throw error;
  }
};
